// Support Cases — the platform operator queue for tenant/partner support tickets
// (ai-agent-service). Cross-tenant list with filters, SLA flags, assignment,
// status transitions and internal notes. P1-6a (docs/admin-support-incidents-scope.md).
import { fmtDate, fmtDateTime } from '../../lib/format';
import React, { useCallback, useEffect, useState } from 'react';
import {
  Button, Card, Drawer, Empty, Input, Select, Space, Table, Tag, Typography, message,
} from 'antd';
import { ReloadOutlined, UserAddOutlined } from '@ant-design/icons';
import { useAppSelector } from '../../app/hooks';
import { selectUser, selectHasPermission } from '../../features/auth/authSlice';
import { PageHeader, StatusTag, useUrlTableState, SavedViews, ExportButton, type CsvColumn } from '../../components/console';
import * as api from '../../services/supportService';

const { Text, Paragraph } = Typography;

const PRIORITY_COLOR: Record<string, string> = {
  urgent: 'error', high: 'warning', normal: 'default', low: 'default',
};
const STATUS_OPTIONS = ['open', 'investigating', 'escalated', 'resolved', 'closed'];
const PRIORITY_OPTIONS = ['urgent', 'high', 'normal', 'low'];

const fmt = (iso?: string | null) => (iso ? fmtDateTime(iso) : '—');

// SLA flag: breached (past due, unresolved) / due-soon (<4h) / ok.
const slaTag = (t: api.SupportTicket) => {
  if (!t.sla_due_at || t.status === 'resolved' || t.status === 'closed') return null;
  const due = new Date(t.sla_due_at).getTime();
  const now = Date.now();
  if (due < now) return <Tag color="error">SLA breached</Tag>;
  if (due - now < 4 * 3600 * 1000) return <Tag color="warning">Due soon</Tag>;
  return null;
};

const SUPPORT_EXPORT_COLUMNS: CsvColumn<api.SupportTicket>[] = [
  { header: 'Ref', key: 'ref' },
  { header: 'Subject', value: (t) => t.subject || '' },
  { header: 'Priority', key: 'priority' },
  { header: 'Status', key: 'status' },
  { header: 'Module', value: (t) => t.module || '' },
  { header: 'Category', value: (t) => t.category || '' },
  { header: 'Reporter', value: (t) => t.reporter_email || '' },
  { header: 'Assignee', value: (t) => t.assignee_email || '' },
  { header: 'SLA due', value: (t) => (t.sla_due_at ? new Date(t.sla_due_at).toISOString() : '') },
  { header: 'Created', value: (t) => (t.created_at ? new Date(t.created_at).toISOString() : '') },
];

export const SupportCasesPage: React.FC = () => {
  const user = useAppSelector(selectUser);
  const canManage = useAppSelector(selectHasPermission('support:manage'));
  const { q, filters, setQ, setFilter } = useUrlTableState(['status', 'priority']);

  const [rows, setRows] = useState<api.SupportTicket[]>([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<(api.SupportTicket & { notes?: api.TicketNote[] }) | null>(null);
  const [drawerBusy, setDrawerBusy] = useState(false);
  const [noteBody, setNoteBody] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setRows(await api.listSupportTickets({
        q: q || undefined,
        status: filters.status || undefined,
        priority: filters.priority || undefined,
        limit: 200,
      }));
    } catch {
      message.error('Could not load support tickets.');
    } finally {
      setLoading(false);
    }
  }, [q, filters.status, filters.priority]);

  useEffect(() => { void load(); }, [load]);

  const openTicket = async (id: string) => {
    try {
      setSelected(await api.getSupportTicket(id));
      setNoteBody('');
    } catch {
      message.error('Could not open the ticket.');
    }
  };

  const patch = async (p: api.TicketPatch, ok: string) => {
    if (!selected) return;
    setDrawerBusy(true);
    try {
      await api.updateSupportTicket(selected.id, p);
      message.success(ok);
      await openTicket(selected.id);
      await load();
    } catch (e: any) {
      message.error(e?.response?.data?.detail || 'Update failed.');
    } finally {
      setDrawerBusy(false);
    }
  };

  const addNote = async () => {
    if (!selected || !noteBody.trim()) return;
    setDrawerBusy(true);
    try {
      await api.addSupportNote(selected.id, noteBody.trim());
      setNoteBody('');
      await openTicket(selected.id);
    } catch {
      message.error('Could not add the note.');
    } finally {
      setDrawerBusy(false);
    }
  };

  return (
    <div className="p-1">
      <PageHeader
        title="Support Cases"
        subtitle="Tenant & partner support tickets across the platform."
        actions={
          <Space wrap>
            <SavedViews pageKey="support-cases" />
            <ExportButton filename="support-cases" rows={rows} columns={SUPPORT_EXPORT_COLUMNS} />
            <Button icon={<ReloadOutlined />} onClick={() => void load()} loading={loading}>Refresh</Button>
          </Space>
        }
      />

      <Card>
        <Space wrap className="mb-3">
          <Select
            allowClear placeholder="Status" style={{ width: 150 }} value={filters.status || undefined}
            onChange={(v) => setFilter('status', v)} options={STATUS_OPTIONS.map((s) => ({ value: s, label: s }))}
          />
          <Select
            allowClear placeholder="Priority" style={{ width: 150 }} value={filters.priority || undefined}
            onChange={(v) => setFilter('priority', v)} options={PRIORITY_OPTIONS.map((s) => ({ value: s, label: s }))}
          />
          <Input.Search
            placeholder="Search ref / subject / reporter" allowClear style={{ width: 280 }}
            defaultValue={q} onSearch={setQ}
          />
        </Space>
        <Table
          size="small"
          rowKey="id"
          loading={loading}
          dataSource={rows}
          pagination={{ pageSize: 20, hideOnSinglePage: true }}
          onRow={(r) => ({ onClick: () => void openTicket(r.id), style: { cursor: 'pointer' } })}
          locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No tickets." /> }}
          columns={[
            { title: 'Ref', dataIndex: 'ref', render: (r: string) => <Text code>{r}</Text> },
            { title: 'Subject', dataIndex: 'subject', ellipsis: true, render: (s: string | null) => s || '—' },
            { title: 'Tenant', dataIndex: 'tenant_id', ellipsis: true, render: (t: string) => <Text code>{t.slice(0, 8)}</Text> },
            { title: 'Priority', dataIndex: 'priority', render: (p: string) => <Tag color={PRIORITY_COLOR[p] || 'default'}>{p}</Tag> },
            { title: 'Status', dataIndex: 'status', render: (s: string) => <StatusTag status={s} /> },
            { title: 'SLA', key: 'sla', render: (_: unknown, t: api.SupportTicket) => slaTag(t) },
            { title: 'Assignee', dataIndex: 'assignee_email', render: (a: string | null) => a || <Text type="secondary">Unassigned</Text> },
            { title: 'Created', dataIndex: 'created_at', render: fmt },
          ]}
        />
      </Card>

      <Drawer
        width={480}
        open={!!selected}
        onClose={() => setSelected(null)}
        title={selected ? `${selected.ref} — ${selected.subject || 'Support ticket'}` : ''}
      >
        {selected && (
          <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            <Space wrap>
              <StatusTag status={selected.status} />
              <Tag color={PRIORITY_COLOR[selected.priority] || 'default'}>{selected.priority}</Tag>
              {slaTag(selected)}
            </Space>
            <div>
              <Text type="secondary">Reporter</Text>
              <div>{selected.reporter_name || selected.reporter_email || '—'}</div>
            </div>
            <div>
              <Text type="secondary">Tenant</Text>
              <div><Text code>{selected.tenant_id}</Text></div>
            </div>
            {selected.summary && (
              <div>
                <Text type="secondary">Summary</Text>
                <Paragraph>{selected.summary}</Paragraph>
              </div>
            )}
            <div>
              <Text type="secondary">Module / Category</Text>
              <div>{[selected.module, selected.category].filter(Boolean).join(' · ') || '—'}</div>
            </div>
            <div>
              <Text type="secondary">SLA due</Text> <span>{fmt(selected.sla_due_at)}</span>
            </div>

            {canManage && (
              <Card size="small" title="Actions">
                <Space direction="vertical" style={{ width: '100%' }}>
                  <Space>
                    <Text>Status</Text>
                    <Select
                      size="small" style={{ width: 160 }} value={selected.status} disabled={drawerBusy}
                      onChange={(v) => void patch({ status: v }, 'Status updated')}
                      options={STATUS_OPTIONS.map((s) => ({ value: s, label: s }))}
                    />
                  </Space>
                  <Space>
                    <Text>Priority</Text>
                    <Select
                      size="small" style={{ width: 160 }} value={selected.priority} disabled={drawerBusy}
                      onChange={(v) => void patch({ priority: v }, 'Priority updated')}
                      options={PRIORITY_OPTIONS.map((s) => ({ value: s, label: s }))}
                    />
                  </Space>
                  <Button
                    size="small" icon={<UserAddOutlined />} disabled={drawerBusy}
                    onClick={() => void patch(
                      { assignee_id: user?.id, assignee_email: user?.email, assignee_name: user?.name },
                      'Assigned to you',
                    )}
                  >
                    Assign to me
                  </Button>
                </Space>
              </Card>
            )}

            <Card size="small" title="Internal notes">
              <Space direction="vertical" style={{ width: '100%' }}>
                {(selected.notes || []).length === 0 ? (
                  <Text type="secondary">No notes yet.</Text>
                ) : (
                  (selected.notes || []).map((n) => (
                    <div key={n.id} style={{ borderBottom: '1px solid var(--border)', paddingBottom: 6 }}>
                      <div className="text-xs text-muted-foreground">{n.author_email || 'operator'} · {fmt(n.created_at)}</div>
                      <div>{n.body}</div>
                    </div>
                  ))
                )}
                {canManage && (
                  <Space.Compact style={{ width: '100%' }}>
                    <Input
                      placeholder="Add an internal note…" value={noteBody}
                      onChange={(e) => setNoteBody(e.target.value)} onPressEnter={() => void addNote()} disabled={drawerBusy}
                    />
                    <Button type="primary" onClick={() => void addNote()} disabled={drawerBusy || !noteBody.trim()}>Add</Button>
                  </Space.Compact>
                )}
              </Space>
            </Card>
          </Space>
        )}
      </Drawer>
    </div>
  );
};

export default SupportCasesPage;
